import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PageShell,
  RowActions,
  SectionCard,
  Skeleton,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  type DataTableColumn,
  type RowActionItem,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { EVER_TV_ROUTE_ROLES } from '../lib/everTvActionRbac';
import {
  useExternalServiceMutation,
  useExternalServicesQuery,
} from '../hooks/useExternalServicesQuery';
import { ExternalServiceEditor } from '../components/ExternalServiceEditor';
import {
  EXTERNAL_SCRIPT_CATEGORY_LABELS,
  EXTERNAL_SCRIPT_STATUS_LABELS,
  SCRIPT_PLACEMENT_LABELS,
  STORE_SCOPE_LABELS,
  STORE_SCOPE_TABS,
  type ExternalScriptStatus,
  type ExternalServiceScript,
  type ExternalServiceScriptPayload,
  type StoreScope,
} from '../types/externalService';

const STATUS_VARIANT: Record<ExternalScriptStatus, 'success' | 'neutral' | 'warning'> = {
  ON: 'success',
  OFF: 'neutral',
  TEST: 'warning',
};

interface ScopeSectionProps {
  scope: StoreScope;
  scripts: ExternalServiceScript[];
  canWrite: boolean;
  saving: boolean;
  onCreate: () => void;
  onEdit: (script: ExternalServiceScript) => void;
  onDelete: (script: ExternalServiceScript) => void;
}

/** 한 상점 스코프의 외부 서비스 목록 + 추가 버튼. */
const ScopeSection = ({
  scope,
  scripts,
  canWrite,
  saving,
  onCreate,
  onEdit,
  onDelete,
}: ScopeSectionProps) => {
  const columns: DataTableColumn<ExternalServiceScript>[] = [
    {
      key: 'serviceName',
      header: '서비스명',
      cell: (row) => (
        <span className="text-sm font-medium" style={{ color: 'var(--content-strong)' }}>
          {row.serviceName}
        </span>
      ),
    },
    {
      key: 'category',
      header: '분류',
      width: 140,
      cell: (row) => (
        <span className="text-sm" style={{ color: 'var(--content-muted)' }}>
          {EXTERNAL_SCRIPT_CATEGORY_LABELS[row.category]}
        </span>
      ),
    },
    {
      key: 'idOrScript',
      header: '측정ID / 스크립트',
      cell: (row) => (
        <span
          className="block max-w-[260px] truncate font-mono text-[12px]"
          style={{ color: 'var(--content-muted)' }}
          title={row.measurementId ?? row.scriptContent ?? ''}
        >
          {row.measurementId ?? (row.scriptContent ? row.scriptContent.slice(0, 80) : '—')}
        </span>
      ),
    },
    {
      key: 'placement',
      header: '위치',
      width: 110,
      cell: (row) => (
        <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          {SCRIPT_PLACEMENT_LABELS[row.placement]}
        </span>
      ),
    },
    {
      key: 'status',
      header: '상태',
      width: 90,
      align: 'center',
      cell: (row) => (
        <StatusBadge
          label={EXTERNAL_SCRIPT_STATUS_LABELS[row.status]}
          variant={STATUS_VARIANT[row.status]}
          size="sm"
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 56,
      align: 'right',
      cell: (row) => {
        if (!canWrite) return null;
        const actions: RowActionItem[] = [
          { key: 'edit', label: '수정', onClick: () => onEdit(row) },
          {
            key: 'delete',
            label: '삭제',
            variant: 'danger' as const,
            separatorBefore: true,
            onClick: () => onDelete(row),
          },
        ];
        return <RowActions triggerLabel="작업" actions={actions} />;
      },
    },
  ];

  return (
    <SectionCard
      title={`${STORE_SCOPE_LABELS[scope]} 상점 외부 서비스`}
      description="해당 상점에 주입할 GA·외부 스크립트입니다. 상태가 노출(ON)인 항목만 프로덕션에 적용됩니다."
      aside={
        canWrite ? (
          <Button size="sm" onClick={onCreate} disabled={saving} data-testid={`ext-add-${scope}`}>
            <Plus size={15} aria-hidden="true" />
            추가
          </Button>
        ) : undefined
      }
    >
      <DataTable
        columns={columns}
        rows={scripts}
        rowKey={(row) => row.id}
        emptyState="등록된 외부 서비스가 없습니다."
        stickyHeader
        ariaLabel={`${STORE_SCOPE_LABELS[scope]} 외부 서비스 목록`}
      />
    </SectionCard>
  );
};

export const ExternalServicesPage = () => {
  const canWrite = useHasRole(EVER_TV_ROUTE_ROLES);
  const query = useExternalServicesQuery();
  const { create, update, remove } = useExternalServiceMutation();

  const [tab, setTab] = useState<StoreScope>('BASE');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ExternalServiceScript | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ExternalServiceScript | null>(null);

  // 탭(상점 스코프)별로 분류. ALL 스코프는 전 상점 공통이라 각 탭에 함께 노출한다.
  const scriptsByScope = useMemo(() => {
    const all = query.data ?? [];
    const map = new Map<StoreScope, ExternalServiceScript[]>();
    for (const scope of STORE_SCOPE_TABS) {
      const rows = all
        .filter((s) => s.storeScope === scope || s.storeScope === 'ALL')
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder);
      map.set(scope, rows);
    }
    return map;
  }, [query.data]);

  const saving = create.isPending || update.isPending;

  const openCreate = () => {
    setEditTarget(null);
    setEditorOpen(true);
  };

  const openEdit = (script: ExternalServiceScript) => {
    setEditTarget(script);
    setEditorOpen(true);
  };

  const handleSubmit = (payload: ExternalServiceScriptPayload) => {
    if (editTarget) {
      update.mutate(
        { id: editTarget.id, payload },
        {
          onSuccess: () => {
            toast.success('외부 서비스가 수정되었습니다.');
            setEditorOpen(false);
          },
          onError: () => toast.error('수정에 실패했습니다.'),
        },
      );
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          toast.success('외부 서비스가 추가되었습니다.');
          setEditorOpen(false);
        },
        onError: () => toast.error('추가에 실패했습니다.'),
      });
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('외부 서비스가 삭제되었습니다.');
        setDeleteTarget(null);
      },
      onError: () => toast.error('삭제에 실패했습니다.'),
    });
  };

  return (
    <PageShell
      title="외부 서비스 설정"
      breadcrumb={[
        { label: 'CMS', href: '/cms/ever-tv' },
        { label: 'EVER TV', href: '/cms/ever-tv' },
        { label: '외부 서비스 설정' },
      ]}
      description="GA·외부 스크립트를 상점별로 등록하고 노출 상태를 제어합니다. 변경 사항은 스토어프론트 전역 레이아웃에 주입됩니다."
      data-testid="external-services-page"
    >
      {query.isError ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>외부 서비스 목록을 불러오지 못했습니다.</p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            다시 시도
          </Button>
        </div>
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as StoreScope)}>
          <TabsList>
            {STORE_SCOPE_TABS.map((scope) => (
              <TabsTrigger key={scope} value={scope} data-testid={`ext-tab-${scope}`}>
                {STORE_SCOPE_LABELS[scope]}
              </TabsTrigger>
            ))}
          </TabsList>

          {STORE_SCOPE_TABS.map((scope) => (
            <TabsContent key={scope} value={scope} className="mt-4">
              {query.isPending ? (
                <div role="status" aria-live="polite" className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-[44px] w-full" />
                  ))}
                </div>
              ) : (
                <ScopeSection
                  scope={scope}
                  scripts={scriptsByScope.get(scope) ?? []}
                  canWrite={canWrite}
                  saving={saving}
                  onCreate={openCreate}
                  onEdit={openEdit}
                  onDelete={setDeleteTarget}
                />
              )}
            </TabsContent>
          ))}
        </Tabs>
      )}

      <ExternalServiceEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        target={editTarget}
        storeScope={tab}
        saving={saving}
        onSubmit={handleSubmit}
      />

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>외부 서비스 삭제</DialogTitle>
            <DialogDescription>
              "{deleteTarget?.serviceName ?? ''}" 외부 서비스를 삭제할까요? 이 작업은 되돌릴 수 없습니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                취소
              </Button>
            </DialogClose>
            <Button variant="danger" size="sm" disabled={remove.isPending} onClick={handleDelete}>
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
